import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import type { OutletAcbCountResponse } from './generated';
import {
  DepartmentRegionCreatePayload,
  DepartmentRegionDeletePayload,
  DepartmentRegionListPayload,
  DepartmentRegionUpdatePayload,
  BreakdownEquipmentTypeCreatePayload,
  BreakdownEquipmentTypeDeletePayload,
  BreakdownEquipmentTypeListPayload,
  BreakdownEquipmentTypeUpdatePayload,
  EquipmentGuidePayload,
  EquipmentRepairsPayload,
  EquipmentScanPayload,
  RequestPayload,
} from './types';
import { BreakdownEquipmentType, DepartmentRegion, Equipment, EquipmentLocation, EquipmentModel, EquipmentModelItem, EquipmentRepairOrder, PaidInstallation } from './models/equipment';


const ENDPOINT = 'equipment/';

/** Swagger описывает 200 без schema; runtime совпадает с count-envelope. */
type EquipmentsServiceCountResponse = OutletAcbCountResponse;

// eslint-disable-next-line import/no-anonymous-default-export
export default (instance: AxiosInstance, config: AxiosRequestConfig) => ({
  getEquipments: (payload: RequestPayload) =>
    instance.post<RequestPayload, AxiosResponse<ApiResponse<Paginated<'data', Equipment>>>>(
      ENDPOINT + 'list/', payload, config),

  getEquipmentsLocations: (payload: RequestPayload) =>
    instance.post<ApiResponse<Paginated<'data', EquipmentLocation>>>(
      ENDPOINT + 'location/', payload, config),

  getEquipmentsOutlet: (outletId: number | string, payload: RequestPayload) =>
    instance.post<ApiResponse<Paginated<'data', Equipment>>>(
      ENDPOINT + `outlet/${outletId}/`, payload, config),

  getEquipmentsGuides: (payload: RequestPayload) =>
    instance.post<ApiResponse<Paginated<'data', EquipmentModel>>>(
      ENDPOINT + 'guide/list/', payload, config),

  getEquipmentModel: (modelId: string) =>
    instance.get<ApiResponse<EquipmentModelItem>>(
      ENDPOINT + `guide/info/${modelId}/`, config),

  updateEquipmentsGuide: (payload: EquipmentGuidePayload) =>
    instance.post(
      ENDPOINT + 'guide/update/', payload, config),

  clearQR: (id: number) =>
    instance.delete(ENDPOINT + 'qrcode/clear/', { ...config, data: { id }}), 

  // SERVICE

  getEquipmentsRepairs: (payload: RequestPayload) =>
    instance.post<
      RequestPayload,
      AxiosResponse<ApiResponse<Paginated<'data', EquipmentRepairOrder>>>
    >(ENDPOINT + 'service/list/', payload, config),

  postEquipmentsRepairs: (payload: EquipmentRepairsPayload) =>
    instance.post<EquipmentRepairOrder>(
      ENDPOINT + 'service/add/',
      payload,
      config
    ),

  getEquipmentsRepair: (id: string) =>
    instance.get(ENDPOINT + `service/info/${id}/`, config),

  putEquipmentsRepair: (payload: EquipmentRepairsPayload) =>
    instance.put<EquipmentRepairOrder>(
      ENDPOINT + 'service/edit/',
      payload,
      config
    ),
 
  deleteEquipmentsRepair: (id: string) =>
    instance.delete<EquipmentRepairOrder>(
      ENDPOINT + `service/delete/${id}/`,
      config
    ),
    
  cancelEquipmentsRepair: (id: string, status_id: number, comment: string) =>
    instance.put<EquipmentRepairOrder>(
      ENDPOINT + 'service/cancel/',
      { id, status_id, comment },
      config
    ),
  setCheckedState: (id: string | number, status_id: number, comment: string) =>
    instance.put(
      ENDPOINT + 'service/set_check/update/',
      { checks: [{ id, checked_status_id: status_id, comment }] },
      config
    ),

  sendEquipmentsRepair: (id: string) =>
    instance.get(ENDPOINT + `service/approval/${id}/`, config),
  getEquipmentsRepairCount: () =>
    instance.get<EquipmentsServiceCountResponse>(ENDPOINT + 'service/count/', config),
  getPaidInstallations: (payload: RequestPayload) =>
    instance.post<ApiResponse<Paginated<'data', PaidInstallation>>>(
      ENDPOINT + 'service/installation_registry/list/', payload, config),

  scanEquipment: (payload: EquipmentScanPayload) =>
    instance.post<ApiResponse<any>>(ENDPOINT + 'scanning/', payload, config),

  // SERVICE DEPARTMENT <-> REGION
  getDepartmentRegions: (payload: DepartmentRegionListPayload) =>
    instance.post<ApiResponse<Paginated<'data', DepartmentRegion>>>(
      ENDPOINT + 'service/department_region_list/', payload, config),
  createDepartmentRegion: (payload: DepartmentRegionCreatePayload) =>
    instance.post<ApiResponse<{ id: number }>>(
      ENDPOINT + 'service/department_create/', payload, config),
  updateDepartmentRegion: (payload: DepartmentRegionUpdatePayload) =>
    instance.post<ApiResponse<{ id: number }>>(
      ENDPOINT + 'service/department_update/', payload, config),
  deleteDepartmentRegion: (payload: DepartmentRegionDeletePayload) =>
    instance.post<ApiResponse<any>>(
      ENDPOINT + 'service/department_delete/', payload, config),

  // SERVICE BREAKDOWN <-> EQUIPMENT TYPE
  getBreakdownEquipmentTypes: (payload: BreakdownEquipmentTypeListPayload) =>
    instance.post<ApiResponse<Paginated<'data', BreakdownEquipmentType>>>(
      ENDPOINT + 'service/breakdown_equipment_type_list/', payload, config),
  createBreakdownEquipmentType: (payload: BreakdownEquipmentTypeCreatePayload) =>
    instance.post<ApiResponse<{ id: number }>>(
      ENDPOINT + 'service/breakdown_equipment_type_create/', payload, config),
  updateBreakdownEquipmentType: (payload: BreakdownEquipmentTypeUpdatePayload) =>
    instance.post<ApiResponse<{ id: number }>>(
      ENDPOINT + 'service/breakdown_equipment_type_update/', payload, config),
  deleteBreakdownEquipmentType: (payload: BreakdownEquipmentTypeDeletePayload) =>
    instance.post<ApiResponse<Record<string, never>>>(
      ENDPOINT + 'service/breakdown_equipment_type_delete/', payload, config),
});
